<template>
  <div class="condition-element" :class="{ 'dropdown-open': hasOpenDropdown }">
    <div class="condition-header">
      <span class="condition-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="16 18 22 12 16 6"></polyline>
          <polyline points="8 6 2 12 8 18"></polyline>
        </svg>
        <span>Condition</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="condition-content">
      <div class="condition-form">
        
        <!-- Conditions List -->
        <div class="conditions-list">
          <div 
            v-for="(cond, idx) in element.conditions" 
            :key="'wrapper-' + idx"
            class="condition-wrapper"
          >
            <!-- Condition Row -->
            <div class="condition-row">
              <div class="condition-inputs">
                <!-- Variable Input -->
                <div class="input-wrapper variable-wrapper js-variable-select" style="position: relative; width: 100%;">
                  <VariableSelector 
                    v-model="cond.variable"
                    :variables="variables"
                    :allow-custom="true"
                    placeholder="Variable"
                    @change="updateElement"
                  />
                </div>

                <!-- Operator Select (Custom Dropdown) -->
                <div class="input-wrapper operator-wrapper">
                  <div 
                    class="custom-select" 
                    :class="{ active: activeDropdown === idx }"
                    @click="toggleDropdown(idx, $event)"
                  >
                    <div class="selected-option">
                      <span class="operator-symbol">{{ getOperatorSymbol(cond.operator) }}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="chevron-icon">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                    
                    <div class="options-dropdown" v-if="activeDropdown === idx">
                      <div 
                        v-for="op in operators" 
                        :key="op.value"
                        class="option-item"
                        :class="{ selected: cond.operator === op.value }"
                        @click.stop="selectOperator(cond, op.value)"
                      >
                        <span class="option-symbol">{{ op.symbol }}</span>
                        <span class="option-label">{{ op.label }}</span>
                        <svg v-if="cond.operator === op.value" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="check-icon">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Value Input -->
                <div 
                  class="input-wrapper value-wrapper" 
                  style="position: relative;"
                  @click="startEditingValue(idx)"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="input-icon">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                  </svg>
                  
                  <!-- Display Mode -->
                  <div 
                    v-if="editingValueIndex !== idx" 
                    class="condition-input value-display-div"
                    :class="getValueClass(cond.value)"
                  >
                    {{ getFormattedDisplayValue(cond.value) || 'Valeur' }}
                  </div>

                  <!-- Edit Mode -->
                  <input 
                    v-else
                    type="text" 
                    v-model="cond.value" 
                    placeholder="Valeur"
                    class="condition-input value-input"
                    :class="getValueClass(cond.value)"
                    @input="handleValueInput(idx, $event)"
                    @blur="stopEditingValue"
                    @change="updateElement"
                    @keyup.enter="$event.target.blur()"
                  />
                  
                  <!-- Insérer une variable -->
                  <div v-if="editingValueIndex === idx" class="variable-inserter-inline" style="position: absolute; right: 0; top: 100%; z-index: 50; width: 200px;">
                    <VariableSelector 
                      v-model="selectedVariableToInsertValue"
                      :variables="variables"
                      placeholder="Insérer variable..."
                      @change="(v) => handleVariableSelectValue(v, idx)"
                    />
                  </div>
                </div>
              </div>

              <button 
                class="remove-condition-btn" 
                @click="removeCondition(idx)"
                v-if="element.conditions.length > 1"
                title="Supprimer la condition"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <!-- Logic Gate Between Rows -->
            <div 
              v-if="idx < element.conditions.length - 1" 
              class="logic-gate-separator"
            >
              <div class="gate-line"></div>
              <div class="gate-options small">
                <label :class="{ active: element.logic_gate === 'AND' }">
                  <input type="radio" value="AND" v-model="element.logic_gate" @change="updateElement">
                  ET
                </label>
                <label :class="{ active: element.logic_gate === 'OR' }">
                  <input type="radio" value="OR" v-model="element.logic_gate" @change="updateElement">
                  OU
                </label>
              </div>
              <div class="gate-line"></div>
            </div>
          </div>
        </div>

        <!-- Add Condition -->
        <div class="condition-controls">
          <button class="add-condition-btn" @click="addCondition">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Ajouter une condition
          </button>
        </div>

        <!-- True Path Redirection -->
        <div class="true-path-section">
          <div class="path-label">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="success-icon">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>Si vrai, rediriger vers :</span>
          </div>
          
          <div class="redirect-select-container">
            <div class="select-wrapper">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="tag-icon">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
              <div class="block-autocomplete-wrapper" style="position: relative; width: 100%;">
                <BlockSelector
                  v-model="element.true_block"
                  :id-bot="idBot"
                  placeholder="Sélectionner un bloc..."
                  @change="updateTrueBlock"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="condition-description">
          Si les conditions sont remplies, le flux sera redirigé vers le bloc sélectionné. Sinon, il continuera vers l'élément suivant.
        </div>

      </div>
    </div>


  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios';
import { consoleError } from '@/utils/logger';
import BlockSelector from '@/components/common/BlockSelector.vue';
import VariableSelector from '@/components/common/VariableSelector.vue';

export default {
  name: 'ConditionElement',
  components: {
    BlockSelector,
    VariableSelector
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    idBot: {
      type: String,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      blocks: [],
      mainBlocks: [],
      standardBlocks: [],
      isLoading: false,
      activeDropdown: null, // Tracks which operator dropdown is open by index
      operators: [
        { value: '==', label: 'Egal à', symbol: '=', icon: 'fa-equals' },
        { value: '!=', label: 'Différent de', symbol: '≠', icon: 'fa-not-equal' },
        { value: '>', label: 'Supérieur à', symbol: '>', icon: 'fa-greater-than' },
        { value: '<', label: 'Inférieur à', symbol: '<', icon: 'fa-less-than' },
        { value: '>=', label: 'Sup. ou égal', symbol: '≥', icon: 'fa-greater-than-equal' },
        { value: '<=', label: 'Inf. ou égal', symbol: '≤', icon: 'fa-less-than-equal' },
        { value: 'contains', label: 'Contient', symbol: '⊂', icon: 'fa-font' },
        { value: 'in', label: 'Est dans', symbol: '∈', icon: 'fa-compress-arrows-alt' }
      ],
      // Autocomplete state
      activeSuggestionIndex: null,
      suggestionContext: null, // 'variable' or 'value'
      selectedVariableToInsertValue: null,
      editingValueIndex: null, // Track which value input is being edited


    }
  },

  computed: {

    hasOpenDropdown() {
      return this.activeDropdown !== null;
    },
  },
  async created() {
    // Les blocs sont désormais chargés par <BlockSelector> lui-même (via id-bot).
    // Plus besoin de loadBlocks() ici (évite un fetch redondant).

    // Initialiser les valeurs par défaut si nécessaire
    if (!this.element.conditions) {
      this.$set(this.element, 'conditions', [{ variable: '', operator: '==', value: '' }]);
    }
    if (!this.element.logic_gate) {
      this.$set(this.element, 'logic_gate', 'AND');
    }
    if (!this.element.true_block) {
      this.$set(this.element, 'true_block', '');
    }
    
    // Close dropdowns on outside click
    document.addEventListener('click', this.closeDropdowns);
    document.addEventListener('click', this.closeSuggestions);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDropdowns);
    document.removeEventListener('click', this.closeSuggestions);
  },
  methods: {
    updateElement() {
      this.$emit('update', this.element);
    },
    
    addCondition() {
      this.element.conditions.push({ variable: '', operator: '==', value: '' });
      this.updateElement();
    },
    
    removeCondition(index) {
      this.element.conditions.splice(index, 1);
      this.updateElement();
    },
    
    toggleDropdown(index, event) {
      event.stopPropagation();
      this.activeDropdown = this.activeDropdown === index ? null : index;
    },
    
    selectOperator(cond, operatorValue) {
      cond.operator = operatorValue;
      this.activeDropdown = null;
      this.updateElement();
    },
    
    closeDropdowns() {
      this.activeDropdown = null;
    },
    
    getOperatorSymbol(value) {
        const op = this.operators.find(o => o.value === value);
        return op ? op.symbol : value;
    },
    
    // Autocomplete & Styling Logic
    
    startEditingValue(index) {
      this.editingValueIndex = index;
      // Focus element after render
      this.$nextTick(() => {
        const input = this.$el.querySelectorAll('.value-input')[index];
        if (input) input.focus();
      });
    },

    stopEditingValue() {
    // Delay to allow click on suggestions
      setTimeout(() => {
        this.editingValueIndex = null;
      }, 200);
    },

    handleVariableSelectValue(variableId, index) {
      if (!variableId) return;
      const cond = this.element.conditions[index];
      const value = cond.value || '';
      cond.value = value + `{{${variableId}}}`;
      this.updateElement();
      
      this.$nextTick(() => {
        this.selectedVariableToInsertValue = null;
      });
    },
    
    getValueClass(value) {
      if (!value) return '';
      // Check if number
      if (!isNaN(value) && !isNaN(parseFloat(value))) return 'val-number';
      // Check if variable
      if (value.startsWith('{{') && value.endsWith('}}')) return 'val-variable';
      // Assume string
      return 'val-string';
    },

    getFormattedDisplayValue(value) {
      if (!value) return '';
      // Check if number
      if (!isNaN(value) && !isNaN(parseFloat(value))) return value;
      // Check if variable
      if (value.startsWith('{{') && value.endsWith('}}')) return value;
      // Assume string - Add quotes
      return `"${value}"`;
    },

    async loadBlocks() {
      if (!this.idBot || this.idBot === 'undefined') {
        this.blocks = [];
        return;
      }
      this.isLoading = true;
      try {
        const userData = localStorage.getItem('user');
        if (!userData) return;
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        const config = {
          headers: { 'Authorization': `Bearer ${token}` }
        };
        
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.idBot}/flow`,
          config
        );
        
        if (response.data && response.data.success) {
          const flowData = response.data.flow;
          
          // Traiter les blocs principaux
          this.mainBlocks = [];
          if (flowData.mainblocks) {
            if (flowData.mainblocks.welcome) {
              this.mainBlocks.push(...flowData.mainblocks.welcome.map(block => ({
                ...block,
                displayName: `Welcome: ${block.blockname || block.idblock}`
              })));
            }
            if (flowData.mainblocks.default) {
              this.mainBlocks.push(...flowData.mainblocks.default.map(block => ({
                ...block,
                displayName: `Default: ${block.blockname || block.idblock}`
              })));
            }
            if (flowData.mainblocks.commerce) {
              this.mainBlocks.push(...flowData.mainblocks.commerce.map(block => ({
                ...block,
                displayName: `Commerce: ${block.blockname || block.idblock}`
              })));
            }
          }
          
          // Traiter les blocs standards
          this.standardBlocks = flowData.blocks || [];
          
          // Combiner tous les blocs
          this.blocks = [...this.mainBlocks, ...this.standardBlocks];
          this.initializeBlockName();
        }
      } catch (error) {
        consoleError('Erreur lors du chargement des blocs:', error);
      } finally {
        this.isLoading = false;
      }
    },
    updateTrueBlock() {
      // Le v-model a déjà mis element.true_block = idblock (string) via update:modelValue.
      // L'événement @change fournit l'OBJET bloc complet — il ne faut PAS l'écrire dans
      // true_block (sinon on stocke un objet au lieu de l'idblock). On propage juste au parent.
      this.updateElement();
    }
  }
}
</script>

<style scoped>
.condition-element {
  background: #ffffff;
  border-radius: 16px;
  /* overflow: hidden; Removed to allow dropdowns to spill out */
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.condition-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(to right, #fff7ed, #ffedd5); /* Orange tint for logic */
  border-bottom: 1px solid rgba(251, 146, 60, 0.1);
  border-radius: 16px 16px 0 0;
}

.condition-type-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #c2410c; /* Orange-700 */
  font-weight: 600;
}

.element-actions {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  background: white;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #64748b;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  color: #c2410c;
}

.delete-btn:hover {
  background: #fef2f2;
  color: #ef4444;
}

.condition-content {
  padding: 1.25rem;
}

.conditions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.condition-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8fafc;
  padding: 0.5rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.condition-inputs {
  display: flex;
  gap: 0.5rem;
  width: 100%; /* Ensure full width usage */
  min-width: 0; /* Allow flex shrinking */
  /* flex-wrap: wrap; Removed to prevent breaking with long text */
}

.input-wrapper {
  position: relative;
  flex: 1;
  min-width: 0; /* Prevent flex overflow */
}

/* Specific wrappers */
.variable-wrapper {
  flex: 0 0 35%; /* Fixed width ratio for variable */
  max-width: 250px;
}



.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.8rem;
  pointer-events: none;
}

.condition-input, .condition-select {
  width: 100%;
  padding: 8px 10px 8px 30px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #334155;
  background: white;
  transition: all 0.2s;
}

.condition-select-input {
  appearance: none; /* Remove default arrow */
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.condition-select {
  padding-left: 10px; /* Moins de padding pour le select sans icone */
  cursor: pointer;
}

.condition-input:focus, .condition-select:focus, .condition-select-input:focus {
  outline: none;
  border-color: #fb923c;
  box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.1);
}

.remove-condition-btn {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.remove-condition-btn:hover {
  opacity: 1;
  background: #fee2e2;
}

.condition-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.add-condition-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px dashed #cbd5e1;
  background: transparent;
  color: #64748b;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.add-condition-btn:hover {
  border-color: #fb923c;
  color: #c2410c;
  background: #fff7ed;
}

.logic-gate-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.gate-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.gate-options {
  display: flex;
  background: #f1f5f9;
  padding: 2px;
  border-radius: 6px;
}

.gate-options label {
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
}

.gate-options input {
  display: none;
}

.gate-options label.active {
  background: white;
  color: #c2410c;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

/* Logic Gate Separator Styles */
.logic-gate-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 0.5rem 0;
  color: #94a3b8;
}

.gate-line {
  height: 1px;
  background: #e2e8f0; /* Gray-200 */
  flex: 1;
}

.gate-options.small {
  background: #f1f5f9;
  padding: 2px;
  border-radius: 6px;
  display: flex;
}

.gate-options.small label {
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
}

.gate-options.small label.active {
  background: white;
  color: #c2410c;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.true-path-section {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 1rem;
}

.path-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #15803d;
  font-weight: 600;
  font-size: 0.875rem;
}

.success-icon {
  color: #16a34a;
}

.redirect-select-container {
  width: 100%;
}

.select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 8px;
  border: 1px solid #bbf7d0;
}

.tag-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
  font-size: 0.8rem;
}

.redirect-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.6rem 0.75rem;
  font-size: 0.875rem;
  color: #1e293b;
  outline: none;
  width: 100%;
}

.block-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  z-index: 201;
  margin-top: 4px;
}

.block-suggestion-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
}

.block-suggestion-item:last-child {
  border-bottom: none;
}

.block-suggestion-item:hover {
  background: #f0fdf4;
  color: #16a34a;
}

.block-name {
  font-weight: 500;
  color: #334155;
}

.block-id {
  font-size: 0.75rem;
  color: #94a3b8;
  font-family: monospace;
}

.condition-description {
  margin-top: 1rem;
  font-size: 0.75rem;
  color: #94a3b8;
  text-align: center;
  font-style: italic;
}

/* Custom Select Styles */
.custom-select {
  position: relative;
  width: 100%;
  height: 38px; /* Match input height roughly */
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}

.custom-select.active {
  border-color: #fb923c;
  box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.1);
}

.selected-option {
  display: flex;
  align-items: center;
  justify-content: center; /* Center content */
  height: 100%;
  padding: 0 8px;
  gap: 6px;
}

.operator-symbol {
  font-weight: 700;
  color: #ea580c; /* Orange-600 */
  font-size: 1.1rem;
}

.chevron-icon {
  font-size: 0.6rem;
  color: #94a3b8;
  transition: transform 0.2s;
}

.custom-select.active .chevron-icon {
  transform: rotate(180deg);
}

.options-dropdown {
  position: absolute;
  top: 110%;
  left: 50%;
  transform: translateX(-50%);
  width: 180px; /* Wider than parent to show labels */
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  z-index: 200;
  max-height: 250px;
  overflow-y: auto;
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translate(-50%, -10px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}

.option-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
  color: #475569;
}

.option-item:hover {
  background: #fff7ed;
  color: #c2410c;
}

.option-item.selected {
  background: #fff7ed;
  color: #c2410c;
  font-weight: 500;
}

.option-symbol {
  width: 24px;
  font-weight: 700;
  font-size: 1rem;
  color: #ea580c;
}

.option-label {
  flex: 1;
  font-size: 0.8rem;
}

.check-icon {
  font-size: 0.7rem;
  color: #ea580c;
}

.operator-wrapper {
  flex: 0 0 60px; /* Smaller fixed width for the compact display */
}

/* Variable Suggestions Styles */
.variable-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 200px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  z-index: 200;
  overflow-y: auto;
  margin-top: 4px;
}

.variable-suggestion-item {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: none;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
}

.variable-suggestion-item:last-child {
  border-bottom: none;
}

.variable-suggestion-item:hover {
  background: #f0f9ff;
}

.var-id {
  font-weight: 500;
  color: #334155;
  font-family: monospace;
}

.var-type {
  font-size: 0.7rem;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 4px;
}

/* Value Highlighting */
.value-input {
  font-weight: 500;
  transition: color 0.2s, border-color 0.2s;
}

.val-number {
  color: #dc2626; /* Red-600 */
}

.val-string {
  color: #16a34a; /* Green-600 */
}

.val-variable {
  color: #ea580c; /* Orange-600 */
  font-weight: 600;
}

.value-display-div {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  width: 100%; /* Force width to match container */
  max-width: 100%; /* Ensure no overflow */
  display: block;
}

/* Ensure wrappers handle overflow correctly */
.value-wrapper {
  min-width: 0;
  overflow: hidden; /* Double ensures child text doesn't force width */
}
</style>
